import React from "react"

type LoginEcgProps = {
    className?: string
    /** viewBox width — the path is generated at this resolution, then stretched. */
    width?: number
    /** viewBox height — the trace is centred on `height / 2`. */
    height?: number
}

/**
 * The slow decorative ECG flourish on the sign-in aside panel.
 *
 * Gaussian beat synthesis ported verbatim from the prototype's
 * `pages/Login/index.js` (`loginEcgPath`), with `**` rewritten as `Math.pow`
 * to stay inside the es5 target the rest of the app compiles to.
 *
 * Purely decorative — this is NOT patient signal, so it is aria-hidden and the
 * stroke simply inherits `currentColor` from the aside.
 */
export default function LoginEcg(props: LoginEcgProps) {
    const {className, width = 600, height = 160} = props;

    const d = React.useMemo(() => {
        const mid = height / 2;
        const beat = 150;
        const g = (t: number, mu: number, s: number, a: number) =>
          a * Math.exp(-Math.pow(t - mu, 2) / (2 * s * s));

        let path = "";
        for (let x = 0; x <= width; x += 2) {
            const t = (x % beat) / beat;
            const v =
              g(t, 0.32, 0.03, 0.1) +
              g(t, 0.47, 0.012, -0.06) +
              g(t, 0.5, 0.014, 0.95) +
              g(t, 0.53, 0.013, -0.14) +
              g(t, 0.78, 0.07, 0.24);
            path += (path ? "L" : "M") + x + " " + (mid - v * 26).toFixed(1);
        }

        return path
    }, [width, height]);

    return (
      <svg
        className={className}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      >
          <path
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
      </svg>
    )
}
